import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2, ShieldCheck, Store, UserRound, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import { useOptions } from '../../hooks/useOptions';
import { useLocationLists, lookupPin } from '../../utils/locationApi';
import API from '../../utils/api';
import './AdminRegistrationModal.css';

const PASSWORD_POLICY_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,}$/;
const PASSWORD_POLICY_MESSAGE = 'Password must be at least 6 characters and include an uppercase letter, a number, and a symbol';
const PROFILE_FOR = ['Myself', 'Son', 'Daughter', 'Brother', 'Sister', 'Relative', 'Friend'];
const MARITAL_STATUS = ['Never Married', 'Unmarried', 'Divorced', 'Widowed', 'Awaiting divorce', 'Separated'];
const localTodayISO = () => {
    const today = new Date();
    const pad = value => String(value).padStart(2, '0');
    return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
};

const initialForm = role => ({
    role,
    name: '', profileFor: 'Myself', profileName: '', gender: '', dateOfBirth: '',
    motherTongue: 'Tamil', religion: '', caste: '', subCaste: '', maritalStatus: 'Never Married',
    businessName: '', ownerName: '', category: '',
    email: '', mobile: '', password: '', confirmPassword: '',
    addressLine1: '', addressLine2: '',
    country: 'India', state: 'Tamil Nadu', district: '', city: '', pincode: '',
    approveNow: false,
});

const Field = ({ label, required = false, full = false, children }) => (
    <label className={`admin-register-field ${full ? 'is-full' : ''}`}>
        <span>{label}{required && <b> *</b>}</span>
        {children}
    </label>
);

const AdminRegistrationModal = ({ open, role, onClose, onCreated }) => {
    const [form, setForm] = useState(() => initialForm(role));
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [pinNote, setPinNote] = useState('');
    const { options: serviceCategories } = useOptions('servicecategory');
    const { options: religions } = useOptions('religion');
    const { options: castes } = useOptions('caste', { parent: form.religion, requireParent: true });
    const { options: subCastes } = useOptions('subcaste', { parent: form.religion && form.caste ? `${form.religion}|${form.caste}` : '', requireParent: true });
    const { countries, states, districts, cities } = useLocationLists(form.country, form.state, form.district);

    useEffect(() => {
        if (!open) return undefined;
        setForm(initialForm(role));
        setShowPassword(false);
        setPinNote('');
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const handleEscape = event => { if (event.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open, role, onClose]);

    if (!open) return null;

    const isProvider = role === 'service';
    const set = (key, value) => setForm(current => ({ ...current, [key]: value }));
    const isIndia = String(form.country || '').trim().toLowerCase() === 'india';

    // Typing a full 6-digit Indian PIN code fills country, state, district and city
    const changePin = (raw) => {
        const pin = isIndia ? raw.replace(/\D/g, '').slice(0, 6) : raw.slice(0, 12);
        set('pincode', pin);
        setPinNote('');
        if (!isIndia || pin.length !== 6) return;
        setPinNote('Finding the place…');
        lookupPin(pin).then(place => {
            if (!place) { setPinNote('PIN code not found. Choose the place yourself.'); return; }
            const district = place.districtOther || place.district;
            setForm(current => (current.pincode === pin ? { ...current, country: place.country, state: place.state, district, city: place.city } : current));
            setPinNote(`Filled from PIN code: ${[place.city, district, place.state].filter(Boolean).join(', ')}`);
        });
    };

    const validate = () => {
        if (!/^\d{10}$/.test(form.mobile)) return 'Enter a valid 10-digit mobile number';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email)) return 'Enter a valid email address';
        if (!PASSWORD_POLICY_REGEX.test(form.password)) return PASSWORD_POLICY_MESSAGE;
        if (form.password !== form.confirmPassword) return 'Passwords do not match';
        if (!form.state || !form.district || !form.city.trim()) return 'Select the state and district, then enter the city';
        if (isProvider && form.pincode && isIndia && !/^\d{6}$/.test(form.pincode)) return 'Enter a valid 6-digit PIN code, or leave it empty';
        if (isProvider) {
            if (!form.businessName.trim() || !form.ownerName.trim() || !form.category) return 'Business name, owner name, and service category are required';
        } else {
            if (!form.name.trim() || !form.gender || !form.dateOfBirth) return 'Name, gender, and date of birth are required';
            if (form.profileFor !== 'Myself' && !form.profileName.trim()) return `Enter the ${form.profileFor.toLowerCase()}'s name`;
            if (!form.religion || !form.caste || !form.subCaste || !form.maritalStatus) return 'Complete the member profile details';
        }
        return '';
    };

    const handleSubmit = async event => {
        event.preventDefault();
        const errorMessage = validate();
        if (errorMessage) {
            toast.error(errorMessage);
            return;
        }

        setSubmitting(true);
        try {
            const common = {
                role,
                email: form.email.trim().toLowerCase(),
                mobile: form.mobile,
                password: form.password,
                country: form.country,
                state: form.state,
                district: form.district,
                city: form.city.trim(),
                isVerified: form.approveNow,
                isApproved: isProvider && form.approveNow,
            };
            const payload = isProvider ? {
                ...common,
                businessName: form.businessName.trim(),
                ownerName: form.ownerName.trim(),
                category: form.category,
                addressLine1: form.addressLine1.trim(),
                addressLine2: form.addressLine2.trim(),
                pincode: form.pincode.trim(),
            } : {
                ...common,
                name: form.name.trim(),
                profileFor: form.profileFor,
                profileName: form.profileFor === 'Myself' ? form.name.trim() : form.profileName.trim(),
                gender: form.gender,
                dateOfBirth: form.dateOfBirth,
                motherTongue: form.motherTongue.trim() || 'Tamil',
                religion: form.religion,
                caste: form.caste,
                subCaste: form.subCaste,
                maritalStatus: form.maritalStatus,
            };

            const response = await API.post('/admin/accounts', payload);
            toast.success(response.data.message);
            await onCreated?.(response.data.user);
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || `Could not create ${isProvider ? 'service provider' : 'member'}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-register-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && !submitting && onClose()}>
            <section className="admin-register-modal" role="dialog" aria-modal="true" aria-labelledby="admin-register-title">
                <header className="admin-register-header">
                    <span className={`admin-register-icon ${isProvider ? 'provider' : 'member'}`}>
                        {isProvider ? <Store size={21} /> : <UserRound size={21} />}
                    </span>
                    <div>
                        <h2 id="admin-register-title">Add {isProvider ? 'service provider' : 'member'}</h2>
                        <p>Create a login account using the standard registration checks. Customer OTP is not required for an admin-created account.</p>
                    </div>
                    <button type="button" className="admin-register-close" onClick={onClose} disabled={submitting} aria-label="Close"><X size={19} /></button>
                </header>

                <form onSubmit={handleSubmit}>
                    <div className="admin-register-body">
                        <div className="admin-register-section-title">{isProvider ? 'Business information' : 'Member information'}</div>
                        <div className="admin-register-grid">
                            {isProvider ? (
                                <>
                                    <Field label="Business name" required><input value={form.businessName} onChange={event => set('businessName', event.target.value)} placeholder="e.g. Sri Murugan Photography" /></Field>
                                    <Field label="Owner / contact person" required><input value={form.ownerName} onChange={event => set('ownerName', event.target.value)} placeholder="Full name" /></Field>
                                    <Field label="Service category" required full><Select value={form.category} onChange={value => set('category', value)} options={serviceCategories} placeholder="Select service category" ariaLabel="Service category" /></Field>
                                </>
                            ) : (
                                <>
                                    <Field label="Account holder name" required><input value={form.name} onChange={event => set('name', event.target.value)} placeholder="Full name" /></Field>
                                    <Field label="Profile created for" required><Select value={form.profileFor} onChange={value => set('profileFor', value)} options={PROFILE_FOR} allowEmpty={false} ariaLabel="Profile created for" /></Field>
                                    {form.profileFor !== 'Myself' && <Field label={`${form.profileFor}'s name`} required><input value={form.profileName} onChange={event => set('profileName', event.target.value)} placeholder="Profile person's full name" /></Field>}
                                    <Field label="Gender" required><Select value={form.gender} onChange={value => set('gender', value)} options={['Male', 'Female']} placeholder="Select gender" ariaLabel="Gender" /></Field>
                                    <Field label="Date of birth" required><DatePicker value={form.dateOfBirth} onChange={value => set('dateOfBirth', value)} placeholder="Select date of birth" max={localTodayISO()} /></Field>
                                    <Field label="Mother tongue" required><input value={form.motherTongue} onChange={event => set('motherTongue', event.target.value)} /></Field>
                                    <Field label="Religion" required><Select value={form.religion} onChange={value => setForm(current => ({ ...current, religion: value, caste: '', subCaste: '' }))} options={religions} placeholder="Select religion" ariaLabel="Religion" /></Field>
                                    <Field label="Caste / division" required><Select value={form.caste} onChange={value => setForm(current => ({ ...current, caste: value, subCaste: '' }))} options={castes} placeholder={form.religion ? 'Select caste' : 'Select religion first'} disabled={!form.religion} ariaLabel="Caste" /></Field>
                                    <Field label="Sub caste" required><Select value={form.subCaste} onChange={value => set('subCaste', value)} options={subCastes} placeholder={form.caste ? 'Select sub caste' : 'Select caste first'} disabled={!form.caste} ariaLabel="Sub caste" /></Field>
                                    <Field label="Marital status" required><Select value={form.maritalStatus} onChange={value => set('maritalStatus', value)} options={MARITAL_STATUS} allowEmpty={false} ariaLabel="Marital status" /></Field>
                                </>
                            )}
                        </div>

                        <div className="admin-register-section-title">Contact and login</div>
                        <div className="admin-register-grid">
                            <Field label="Mobile number" required><div className="admin-register-phone"><span>+91</span><input type="tel" inputMode="numeric" maxLength={10} value={form.mobile} onChange={event => set('mobile', event.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit number" /></div></Field>
                            <Field label="Email address" required><input type="email" value={form.email} onChange={event => set('email', event.target.value)} placeholder="name@example.com" /></Field>
                            <Field label="Password" required><div className="admin-register-password"><input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={form.password} onChange={event => set('password', event.target.value)} placeholder="Uppercase, number and symbol" /><button type="button" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <EyeOff size={17} /> : <Eye size={17} />}</button></div></Field>
                            <Field label="Confirm password" required><input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={form.confirmPassword} onChange={event => set('confirmPassword', event.target.value)} placeholder="Re-enter password" /></Field>
                        </div>

                        <div className="admin-register-section-title">Location and status</div>
                        <div className="admin-register-grid">
                            {isProvider && <Field label="Address line 1" full><input value={form.addressLine1} onChange={event => set('addressLine1', event.target.value)} placeholder="Building, street" maxLength={200} /></Field>}
                            {isProvider && <Field label="Address line 2" full><input value={form.addressLine2} onChange={event => set('addressLine2', event.target.value)} placeholder="Area, landmark (optional)" maxLength={200} /></Field>}
                            <Field label="Country" required><Select value={form.country} onChange={value => setForm(current => ({ ...current, country: value, state: '', district: '', city: '' }))} options={countries} placeholder="Select country" ariaLabel="Country" /></Field>
                            <Field label="State" required><Select value={form.state} onChange={value => setForm(current => ({ ...current, state: value, district: '', city: '' }))} options={states} placeholder={form.country ? 'Select state' : 'Select country first'} disabled={!form.country} ariaLabel="State" /></Field>
                            <Field label="District" required><Select value={form.district} onChange={value => setForm(current => ({ ...current, district: value, city: '' }))} options={districts} placeholder={form.state ? 'Select district' : 'Select state first'} disabled={!form.state} ariaLabel="District" /></Field>
                            <Field label="City" required full><Select value={form.city} onChange={value => set('city', value)} options={cities} placeholder={form.district ? 'Select city' : 'Select district first'} disabled={!form.district} ariaLabel="City" /></Field>
                            {isProvider && (
                                <Field label={isIndia ? 'PIN code' : 'Postal code'} full>
                                    <input value={form.pincode} onChange={event => changePin(event.target.value)} inputMode={isIndia ? 'numeric' : 'text'} maxLength={12} placeholder={isIndia ? '6-digit PIN code (optional)' : 'Postal code (optional)'} />
                                    {pinNote && <small style={{ display: 'block', marginTop: '5px', color: 'var(--admin-text-muted)' }}>{pinNote}</small>}
                                </Field>
                            )}
                            <label className="admin-register-approval is-full">
                                <input type="checkbox" checked={form.approveNow} onChange={event => set('approveNow', event.target.checked)} />
                                <span><ShieldCheck size={18} /><span><strong>{isProvider ? 'Verify and approve provider immediately' : 'Mark member as verified'}</strong><small>Leave unchecked to create the account with pending verification status.</small></span></span>
                            </label>
                        </div>
                    </div>

                    <footer className="admin-register-footer">
                        <span>The account can sign in immediately with the mobile number and password.</span>
                        <div>
                            <button type="button" className="admin-register-cancel" onClick={onClose} disabled={submitting}>Cancel</button>
                            <button type="submit" className="admin-register-submit" disabled={submitting}>
                                {submitting ? <Loader2 size={16} className="admin-register-spin" /> : isProvider ? <Store size={16} /> : <UserRound size={16} />}
                                {submitting ? 'Creating...' : `Create ${isProvider ? 'Provider' : 'Member'}`}
                            </button>
                        </div>
                    </footer>
                </form>
            </section>
        </div>
    );
};

export default AdminRegistrationModal;
